import { Dispatch } from "react";

/**
 * Operation constants for the debounce stack reducer
 */
export const SET_VALUE = "SET_VALUE";
export const UPDATE_INDEX = "UPDATE_INDEX";
export const RESET_FLAGS = "RESET_FLAGS";
export const SYNC_FROM_PARENT = "SYNC_FROM_PARENT";
export const FORCE_WITH_VALUE = "FORCE_WITH_VALUE";
export const CLEAR_ON_CHANGE_PAYLOAD = "CLEAR_ON_CHANGE_PAYLOAD";

/**
 * State interface for the debounce stack hook
 */
export interface DebounceStackState<T> {
  /** The stack of values for undo/redo functionality */
  stack: T[];
  /** The current index in the stack */
  index: number;
  /** Flag indicating if the value was changed internally (not from top-down) */
  changed: boolean;
  /** Flag indicating if the comparison function detected a change */
  comparisonChanged: boolean;
  /** Payload for pending onChange callback */
  onChangePayload: { value: T; index: number; stack: T[] } | null;
}

/**
 * Action types for the debounce stack reducer
 */
export type DebounceStackAction<T> =
  | {
      operation: typeof SET_VALUE;
      payload: { newValue: T; addToStack: boolean };
    }
  | { operation: typeof UPDATE_INDEX; payload: { index: number } }
  | { operation: typeof RESET_FLAGS }
  | {
      operation: typeof SYNC_FROM_PARENT;
      payload: { newValue: T; addToStack: boolean; triggerOnChange: boolean };
    }
  | {
      operation: typeof FORCE_WITH_VALUE;
      payload: { newValue: T; addToStack: boolean; triggerOnChange: boolean };
    }
  | { operation: typeof CLEAR_ON_CHANGE_PAYLOAD };

/**
 * Props interface for the useDebounceStack hook.
 *
 * @template T The type of the value being managed.
 */
export interface UseDebounceStackProps<T> {
  /** The initial value to be managed. */
  value: T;
  /** A dispatch function to update the parent value after debounce or on force update. */
  setValue: Dispatch<T>;
  /** The debounce delay in milliseconds. Defaults to 500. If null, updates are immediate. If negative, updates only occur on forced updates. */
  debounceMilliseconds?: number | null;
  /** Optional callback invoked when the value changes (immediately on setCurrentValue). */
  onChange?: (newValue: T, index: number, stack: ReadonlyArray<T>) => void;
  /** Optional function to compare values for stack updates. Defaults to lodash.isEqual. */
  comparisonFunction?: (oldValue: T, newValue: T) => boolean;
  /** Optional function to stop setValue being triggered when debounce is null. */
  blockReturnFunction?: (currentValue: T) => boolean;
  /** Optional function to stop onChange being triggered on top down changes */
  blockOnChange?: (oldValue: T, newValue: T) => boolean;
  /** Optional function to stop debounce being triggered on top down changes */
  blockDebounceReturn?: (currentValue: T) => boolean;
}

/**
 * Return type for the useDebounceStack hook.
 *
 * @template T The type of the value being managed.
 */
export interface UseDebounceStackReturn<T> {
  /** The current (possibly debounced) value from the stack. */
  currentValue: T;
  /** Function to set the current value (debounced). */
  setCurrentValue: Dispatch<T>;
  /** Function to immediately update the value, bypassing debounce. */
  forceUpdate: (newValue?: T) => void;
  /** Function to revert to a previous value in the stack. */
  undo: (steps?: number) => void;
  /** Function to advance to a more recent value in the stack. */
  redo: (steps?: number) => void;
  /** The full readonly stack of values. */
  stack: ReadonlyArray<T>;
  /** The current numeric index pointing at the active item in the stack. */
  index: number;
}
